import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PickerProvider, type PickerApi } from '@/lib/forms/PickerContext';
import type { MediaAsset } from '@/lib/api/types';
import { Dialog } from '@/lib/ui/Dialog';
import { ErrorBoundary } from '@/lib/ui/ErrorBoundary';
import { mediaApi, ACCEPTED_EXTENSIONS } from './api';
import { uploadIssue, useUploadLimits } from './limits';
import { formatBytes } from '@/lib/format';
import { hasErrorCode } from '@/features/shared/governance';
import { isAppError } from '@/lib/api/errors';
import { keys } from '@/lib/query/keys';
import { MediaBrowser } from './MediaBrowser';
import { FocalPointControl } from './FocalPointDialog';
import { EntityPickerModal } from '@/features/content/EntityPickerModal';
import { previewUrl } from '@/lib/media/derivatives';

/**
 * Hosts the media + entity picker modals and exposes imperative pick() resolvers to the
 * schema-form widgets via PickerProvider (plan §4.7). Decouples forms from the media module.
 *
 * MOUNT IT INSIDE THE ROUTER. It renders real UI — a modal with a full media grid —
 * and that grid is code like any other page's: it may read router state, and it may
 * throw. Mounted in `Providers`, above `<BrowserRouter>` and above every error
 * boundary, pressing "Choose" in an editor took the whole console down to an empty
 * `#root` and cost the editor everything they had typed (F-28). Its mount point is
 * `App`, inside `RouteErrorBoundary`.
 */
export function MediaPickerHost({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [mediaOpen, setMediaOpen] = useState(false);
  // Whether the field that opened the picker trims its uploads (round 8: a partner's logo).
  const [mediaTrim, setMediaTrim] = useState<'transparent' | undefined>(undefined);
  const [entityOpen, setEntityOpen] = useState<{ collection: string } | null>(null);
  const mediaResolver = useRef<((id: string | null) => void) | null>(null);
  const entityResolver = useRef<((id: string | null) => void) | null>(null);

  const pickMedia = useCallback<PickerApi['pickMedia']>((opts) => {
    setMediaTrim(opts?.trim);
    setMediaOpen(true);
    return new Promise<string | null>((resolve) => {
      mediaResolver.current = resolve;
    });
  }, []);

  const pickEntity = useCallback<PickerApi['pickEntity']>((collection) => {
    setEntityOpen({ collection });
    return new Promise<string | null>((resolve) => {
      entityResolver.current = resolve;
    });
  }, []);

  /*
   * Read the asset's size straight out of the query CACHE rather than firing a
   * request of its own. `MediaPreview` already fetches exactly this asset under
   * exactly this key to draw the thumbnail beside the picker, so by the time a
   * gate notice could be shown the row is usually there — and on the pass where
   * it is not, returning null means the form stays quiet instead of accusing a
   * perfectly good photograph of being too small while its record is in flight.
   * `useQueryClient` + `getQueryData` is a synchronous read; a `useQuery` here
   * would duplicate the request and re-render the whole form on every settle.
   */
  const queryClient = useQueryClient();
  const mediaDimensions = useCallback<PickerApi['mediaDimensions']>(
    (id) => {
      const asset = queryClient.getQueryData<MediaAsset>(keys.media.detail(id));
      if (!asset || typeof asset.width !== 'number' || typeof asset.height !== 'number') {
        return null;
      }
      return { width: asset.width, height: asset.height };
    },
    [queryClient],
  );

  const limits = useUploadLimits();
  const checkUpload = useCallback<PickerApi['checkUpload']>(
    (file) => {
      const issue = uploadIssue(file, limits, ACCEPTED_EXTENSIONS);
      if (!issue) return null;
      return issue.kind === 'type'
        ? t('media.unsupportedType', { ext: issue.ext })
        : t('media.exceedsLimit', { limit: formatBytes(issue.limit) });
    },
    [limits, t],
  );

  const uploadImage = useCallback<PickerApi['uploadImage']>(
    async (file, alt) => {
      try {
        const asset = await mediaApi.upload({ file, alt: { en: alt, ar: '' } });
        void queryClient.invalidateQueries({ queryKey: keys.media.all() });
        // Seed the detail cache so the new row's thumbnail and size check need
        // no second request.
        queryClient.setQueryData(keys.media.detail(asset.id), asset);
        return asset.id;
      } catch (e) {
        if (hasErrorCode(e, 'unsafe_svg')) throw new Error(t('media.unsafeSvgDetail'), { cause: e });
        throw new Error(
          isAppError(e) && e.message ? e.message : t('media.uploadFailed'),
          { cause: e },
        );
      }
    },
    [queryClient, t],
  );

  const api: PickerApi = {
    pickMedia,
    pickEntity,
    mediaDimensions,
    checkUpload,
    uploadImage,
    MediaPreview: ({ id }) => <MediaPreview id={id} />,
    FocalControl: ({ id, disabled }) => <FocalPointControl id={id} disabled={disabled} />,
  };

  const closeMedia = (id: string | null) => {
    mediaResolver.current?.(id);
    mediaResolver.current = null;
    setMediaOpen(false);
  };
  const closeEntity = (id: string | null) => {
    entityResolver.current?.(id);
    entityResolver.current = null;
    setEntityOpen(null);
  };

  return (
    <PickerProvider value={api}>
      {children}
      <Dialog
        open={mediaOpen}
        onOpenChange={(o) => !o && closeMedia(null)}
        title={t('media.chooseMedia')}
        size="xl"
      >
        {/* The picker is opened from a half-filled editor, so a crash in the grid
            must cost the grid and nothing else: the boundary keeps the modal — and
            the unsaved form underneath it — alive and closable. */}
        <ErrorBoundary>
          <MediaBrowser
            inModal
            canUpload
            uploadTrim={mediaTrim}
            onSelect={(asset) => closeMedia(asset.id)}
          />
        </ErrorBoundary>
      </Dialog>
      {entityOpen && (
        <EntityPickerModal
          collection={entityOpen.collection}
          open
          onClose={(id) => closeEntity(id)}
        />
      )}
    </PickerProvider>
  );
}

function MediaPreview({ id }: { id: string | null }) {
  const query = useQuery({
    queryKey: keys.media.detail(id ?? 'none'),
    queryFn: () => mediaApi.get(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
  if (!id) return <div className="h-[44px] w-[44px] rounded-sm bg-canvas-deep-sand" aria-hidden />;
  const asset = query.data;
  if (asset?.kind === 'image' && !asset.is_restricted) {
    return (
      <img
        /* The generated thumb, by label — never by index (QA F-13). */
        src={previewUrl(asset)}
        alt={asset.alt?.en ?? ''}
        className="h-[44px] w-[44px] rounded-sm object-cover"
      />
    );
  }
  return (
    <div className="flex h-[44px] w-[44px] items-center justify-center rounded-sm bg-canvas-deep-sand text-ink-mute">
      {asset ? '📄' : '…'}
    </div>
  );
}
